import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  Cpu,
  FolderKanban,
  LayoutGrid,
  Lightbulb,
  PlugZap,
  Search,
  Shield,
  Users,
  Waypoints,
  type LucideIcon,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { MENU } from "./labels";

export interface MenuItem {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Other paths that belong to this item (old URLs and detail pages). */
  also?: string[];
}

/** The new look's main menu, in order (docs/new-look/PLAN.md, step 1.1). */
export const MENU_ITEMS: MenuItem[] = [
  { href: "/dashboard", label: MENU.dashboard, icon: BarChart3 },
  { href: "/team", label: MENU.teamBoard, icon: LayoutGrid },
  { href: "/ideas", label: MENU.ideas, icon: Lightbulb, also: ["/epic-requests"] },
  { href: "/blueprints", label: MENU.blueprints, icon: Waypoints, also: ["/epics", "/features", "/tasks"] },
  { href: "/devices", label: MENU.devices, icon: Cpu },
  { href: "/connections", label: MENU.connections, icon: PlugZap },
  { href: "/teams", label: MENU.teams, icon: Users },
  { href: "/projects", label: MENU.projects, icon: FolderKanban },
];

const ADMIN_ITEM: MenuItem = { href: "/admin/users", label: "Admin", icon: Shield, also: ["/admin"] };

function under(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`);
}

export function isCurrent(item: MenuItem, pathname: string): boolean {
  return [item.href, ...(item.also ?? [])].some((base) => under(pathname, base));
}

function openSearch(): void {
  const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);
  document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: isMac, ctrlKey: !isMac }));
}

/**
 * The desktop menu: a floating bar at the bottom of the page. Labels show from
 * large screens up; tablets get icons with tooltips so the bar stays on one line.
 */
export function NewLookMenu() {
  const { pathname } = useLocation();
  const { user } = useAuthStore();
  const items = user?.isGlobalAdmin ? [...MENU_ITEMS, ADMIN_ITEM] : MENU_ITEMS;

  return (
    <nav
      aria-label="Main"
      className="fixed bottom-4 left-1/2 z-50 flex -translate-x-1/2 items-center gap-0.5 rounded-2xl border border-border bg-background/90 p-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.12)] backdrop-blur-xl"
    >
      {items.map((item) => {
        const current = isCurrent(item, pathname);
        return (
          <Tooltip key={item.href}>
            <TooltipTrigger asChild>
              <Link
                to={item.href}
                aria-label={item.label}
                aria-current={current ? "page" : undefined}
                className={cn(
                  "flex h-11 items-center gap-1.5 whitespace-nowrap rounded-xl px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  current
                    ? "bg-accent font-semibold text-accent-foreground"
                    : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                {/* Hidden below lg (icons only); not sr-only, which resets white-space and let "Team board" wrap. */}
                <span aria-hidden="true" className="hidden whitespace-nowrap lg:inline">
                  {item.label}
                </span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs lg:hidden">
              {item.label}
            </TooltipContent>
          </Tooltip>
        );
      })}

      <span aria-hidden="true" className="mx-1 h-5 w-px bg-border" />

      <button
        type="button"
        onClick={openSearch}
        aria-label="Search (⌘K)"
        className="flex h-11 w-11 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Search className="h-[18px] w-[18px]" aria-hidden="true" />
      </button>
    </nav>
  );
}
